import type { operations } from '@molchanovs/api-contract';

import { createQuery } from '@shared/lib/query-helpers';
import {
  crmRegistrationResponseSchema,
  type RegistrationCollectionResponse,
} from '@entities/registration';
export type { RegistrationCollectionResponse } from '@entities/registration';

import {
  type CrmCustomerLearningFilters,
  type CrmCustomerLearningSection,
  type CrmCustomerLearningSort,
} from './schemas';

type CoursesOperation = operations['getApiV1Courses'];
type CourseStatusesOperation = operations['getApiV1CourseStatuses'];
type CourseTypesOperation = operations['getApiV1CourseTypes'];
type RegistrationsOperation = operations['getApiV1Registrations'];
type RegistrationPaymentStatusesOperation = operations['getApiV1RegistrationPaymentStatuses'];
type RegistrationReferenceTypesOperation = operations['getApiV1RegistrationReferenceTypes'];
type RegistrationTypesOperation = operations['getApiV1RegistrationTypes'];
type OrganizationsOperation = operations['getApiV1Organizations'];
type CertificationsOperation = operations['getApiV1Certifications'];
type CertificationTypesOperation = operations['getApiV1CertificationTypes'];
type EducationModuleLogsOperation = operations['getUserProfileEducationModuleLogs'];

export type CourseCollectionResponse =
  CoursesOperation['responses'][200]['content']['application/json'];
export type CourseStatusCatalogResponse =
  CourseStatusesOperation['responses'][200]['content']['application/json'];
export type CourseTypeCatalogResponse =
  CourseTypesOperation['responses'][200]['content']['application/json'];
/**
 * The Learning Registration table retains the established numbered response.
 * The same operation's EM Log selector branch is represented separately below.
 */
export type RegistrationPaymentStatusCatalogResponse =
  RegistrationPaymentStatusesOperation['responses'][200]['content']['application/json'];
export type RegistrationReferenceTypeCatalogResponse =
  RegistrationReferenceTypesOperation['responses'][200]['content']['application/json'];
export type RegistrationTypeCatalogResponse =
  RegistrationTypesOperation['responses'][200]['content']['application/json'];
export type OrganizationSelectorResponse =
  OrganizationsOperation['responses'][200]['content']['application/json'];
export type CertificationCollectionResponse =
  CertificationsOperation['responses'][200]['content']['application/json'];
export type CertificationTypesResponse =
  CertificationTypesOperation['responses'][200]['content']['application/json'];
/** CRM's UUID history branch always returns an exact filtered total. */
export type EducationModuleLogCollectionResponse = Extract<
  EducationModuleLogsOperation['responses'][200]['content']['application/json'],
  { meta: { totalItems: number } }
>;
type EducationModuleLogResultsOperation = operations['getApiV1EducationModuleLogsResults'];
type EducationModuleLogTypesOperation = operations['getApiV1EducationModuleLogsTypes'];
export type EducationModuleLogResultCatalogResponse =
  EducationModuleLogResultsOperation['responses'][200]['content']['application/json'];
export type EducationModuleLogTypeCatalogResponse =
  EducationModuleLogTypesOperation['responses'][200]['content']['application/json'];
/** The Registration selector uses the same numbered collection envelope. */
export type CrmCustomerLearningRegistrationSelectorResponse =
  RegistrationsOperation['responses'][200]['content']['application/json'];

export const crmCustomerLearningRegistrationSelectorPageSize = 10 as const;
/** CRM EM Logs use the approved five-row opaque-cursor collection. */
export const crmCustomerLearningEmLogsPageSize = 5 as const;

export type CrmCustomerLearningQueryParams = CrmCustomerLearningFilters & {
  cursor?: string;
  customerId: string;
  locale: 'en' | 'ru';
  page: number;
  q?: string;
  section: CrmCustomerLearningSection;
  sort: CrmCustomerLearningSort;
  viewerAccessScope: string;
};

export const crmCustomerLearningKeys = {
  all: ['crm-customer-learning'] as const,
  emLogs: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      params.customerId,
      'em-logs',
      params.locale,
      params.cursor ?? '',
      params.attemptedFrom ?? '',
      params.attemptedTo ?? '',
      params.courseTypeId ?? '',
      params.instructorId ?? '',
      params.registrationId ?? '',
      params.learningItemId ?? '',
      params.result ?? '',
      params.type ?? '',
      params.q ?? '',
      emLogSort(params.sort),
    ] as const,
  emLogResults: (params: CrmCustomerLearningQueryParams) =>
    [...crmCustomerLearningKeys.all, params.viewerAccessScope, 'em-log-results'] as const,
  emLogTypes: (params: CrmCustomerLearningQueryParams) =>
    [...crmCustomerLearningKeys.all, params.viewerAccessScope, 'em-log-types'] as const,
  courses: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      params.customerId,
      'courses',
      params.locale,
      params.page,
      params.courseStatus ?? '',
      params.courseTypeId ?? '',
      params.dateFrom ?? '',
      params.dateTo ?? '',
      params.instructorId ?? '',
      params.schoolId ?? '',
      params.q ?? '',
      params.sort === 'courseStartAt' || params.sort === '-courseStartAt' ? params.sort : '',
    ] as const,
  courseSelector: (
    params: Pick<
      CrmCustomerLearningQueryParams,
      'customerId' | 'locale' | 'q' | 'viewerAccessScope'
    >
  ) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      params.customerId,
      'course-selector',
      params.locale,
      params.q ?? '',
    ] as const,
  courseStatuses: (params: CrmCustomerLearningQueryParams) =>
    [...crmCustomerLearningKeys.all, params.viewerAccessScope, 'course-statuses'] as const,
  courseTypes: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      'course-types',
      params.locale,
    ] as const,
  registrations: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      params.customerId,
      'registrations',
      params.locale,
      params.page,
      params.certifiedFrom ?? '',
      params.certifiedTo ?? '',
      params.costMax ?? '',
      params.costMin ?? '',
      params.courseId ?? '',
      params.courseTypeId ?? '',
      params.instructorId ?? '',
      params.instructorPaymentStatus ?? '',
      params.registrationReferenceType ?? '',
      params.registrationType ?? '',
      params.schoolId ?? '',
      params.studentPaymentStatus ?? '',
      params.submittedFrom ?? '',
      params.submittedTo ?? '',
      params.q ?? '',
      isRegistrationPublicSort(params.sort) ? params.sort : '',
    ] as const,
  registrationPaymentStatuses: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      'registration-payment-statuses',
    ] as const,
  registrationReferenceTypes: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      'registration-reference-types',
    ] as const,
  registrationSelector: (
    params: Pick<CrmCustomerLearningQueryParams, 'customerId' | 'q' | 'viewerAccessScope'>
  ) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      params.customerId,
      'registration-selector',
      params.q ?? '',
      crmCustomerLearningRegistrationSelectorPageSize,
    ] as const,
  registrationSelectorPage: (
    params: Pick<CrmCustomerLearningQueryParams, 'customerId' | 'q' | 'viewerAccessScope'> & {
      page: number;
    }
  ) => [...crmCustomerLearningKeys.registrationSelector(params), params.page] as const,
  registrationTypes: (params: CrmCustomerLearningQueryParams) =>
    [...crmCustomerLearningKeys.all, params.viewerAccessScope, 'registration-types'] as const,
  organizations: (params: CrmCustomerLearningQueryParams) =>
    [...crmCustomerLearningKeys.all, params.viewerAccessScope, 'organizations'] as const,
  certifications: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      params.customerId,
      'certifications',
      params.locale,
      params.page,
      params.certificationTypeId ?? '',
      params.certifiedFrom ?? '',
      params.certifiedTo ?? '',
      params.instructorId ?? '',
      params.q ?? '',
      params.sort.startsWith('certifiedAt') ||
      params.sort.startsWith('expiresAt') ||
      params.sort.startsWith('certificationCode')
        ? params.sort
        : '',
    ] as const,
  certificationTypes: (params: CrmCustomerLearningQueryParams) =>
    [
      ...crmCustomerLearningKeys.all,
      params.viewerAccessScope,
      'certification-types',
      params.locale,
    ] as const,
};

function emLogSort(sort: CrmCustomerLearningSort) {
  if (sort === 'attemptDate.asc') return 'attemptedAt.asc' as const;
  if (sort === 'attemptResult.asc') return 'result.asc' as const;
  if (sort === 'attemptResult.desc') return 'result.desc' as const;
  if (sort === 'attemptScore.asc') return 'scorePoints.asc' as const;
  if (sort === 'attemptScore.desc') return 'scorePoints.desc' as const;
  return 'attemptedAt.desc' as const;
}

/** Canonical EM Log collection; its cursor and stable page size are explicit. */
export function crmCustomerLearningEmLogsQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<EducationModuleLogCollectionResponse>({
    path: `/api/v1/user-profiles/${encodeURIComponent(params.customerId)}/education-module-logs`,
    queryKey: crmCustomerLearningKeys.emLogs(params),
    requestOptions: {
      params: {
        courseTypeId: params.courseTypeId,
        attemptedFrom: params.attemptedFrom,
        attemptedTo: params.attemptedTo,
        cursor: params.cursor,
        instructorId: params.instructorId,
        locale: params.locale,
        moduleId: params.learningItemId,
        limit: crmCustomerLearningEmLogsPageSize,
        q: params.q,
        registrationId: params.registrationId,
        result: params.result,
        sort: emLogSort(params.sort),
        type: params.type,
      },
    },
  });
}

/** Independent catalogue request: never receives EM Log collection state. */
export function crmCustomerLearningEmLogResultsQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<EducationModuleLogResultCatalogResponse>({
    path: '/api/v1/education-module-logs/results',
    queryKey: crmCustomerLearningKeys.emLogResults(params),
  });
}

/** Independent catalogue request: never receives EM Log collection state. */
export function crmCustomerLearningEmLogTypesQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<EducationModuleLogTypeCatalogResponse>({
    path: '/api/v1/education-module-logs/types',
    queryKey: crmCustomerLearningKeys.emLogTypes(params),
  });
}

/** Thin owner catalogue: Course status is independent of the Learning collection. */
export function crmCustomerLearningCourseStatusesQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<CourseStatusCatalogResponse>({
    path: '/api/v1/courses/statuses',
    queryKey: crmCustomerLearningKeys.courseStatuses(params),
  });
}

/** Thin owner catalogue: Course Type display is the canonical label. */
export function crmCustomerLearningCourseTypesQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<CourseTypeCatalogResponse>({
    path: '/api/v1/course-types',
    queryKey: crmCustomerLearningKeys.courseTypes(params),
    requestOptions: { params: { locale: params.locale } },
  });
}

export function crmCustomerLearningCoursesQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<CourseCollectionResponse>({
    path: '/api/v1/courses',
    queryKey: crmCustomerLearningKeys.courses(params),
    requestOptions: {
      params: {
        courseStatus: params.courseStatus,
        courseTypeId: params.courseTypeId,
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        instructorId: params.instructorId,
        locale: params.locale,
        page: params.page,
        q: params.q,
        schoolId: params.schoolId,
        sort: coursePublicSort(params.sort),
        userProfileId: params.customerId,
      },
    },
  });
}

/**
 * The registration Course control reads the canonical Course collection on its
 * own. It intentionally does not inherit Registration filters or table q.
 */
export function crmCustomerLearningCourseSelectorQuery(
  params: Pick<CrmCustomerLearningQueryParams, 'customerId' | 'locale' | 'q' | 'viewerAccessScope'>,
  page = 1
) {
  return createQuery<CourseCollectionResponse>({
    path: '/api/v1/courses',
    queryKey: [...crmCustomerLearningKeys.courseSelector(params), page],
    requestOptions: {
      params: {
        locale: params.locale,
        page,
        q: params.q,
        sort: ['-courseStartAt', '-id'],
        userProfileId: params.customerId,
      },
    },
  });
}

export function crmCustomerLearningRegistrationsQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<RegistrationCollectionResponse>({
    path: '/api/v1/registrations',
    schema: crmRegistrationResponseSchema,
    queryKey: crmCustomerLearningKeys.registrations(params),
    requestOptions: {
      params: {
        certifiedFrom: params.certifiedFrom,
        certifiedTo: params.certifiedTo,
        costMax: params.costMax,
        costMin: params.costMin,
        courseId: params.courseId,
        courseTypeId: params.courseTypeId,
        instructorId: params.instructorId,
        instructorPaymentStatus: params.instructorPaymentStatus,
        locale: params.locale,
        page: params.page,
        q: params.q,
        registrationReferenceType: params.registrationReferenceType,
        registrationType: params.registrationType,
        schoolId: params.schoolId,
        sort: isRegistrationPublicSort(params.sort) ? params.sort : undefined,
        studentPaymentStatus: params.studentPaymentStatus,
        submittedFrom: params.submittedFrom,
        submittedTo: params.submittedTo,
        userProfileId: params.customerId,
      },
    },
  });
}

function coursePublicSort(
  sort: CrmCustomerLearningSort
): readonly ['courseStartAt' | '-courseStartAt', 'id' | '-id'] | undefined {
  if (sort === 'courseStartAt') return ['courseStartAt', 'id'];
  if (sort === '-courseStartAt') return ['-courseStartAt', '-id'];
  return undefined;
}

function isRegistrationPublicSort(sort: CrmCustomerLearningSort): boolean {
  return new Set([
    'submittedAt',
    '-submittedAt',
    'certifiedAt',
    '-certifiedAt',
    'registrationNumber',
    '-registrationNumber',
    'studentPaymentStatus',
    '-studentPaymentStatus',
    'instructorPaymentStatus',
    '-instructorPaymentStatus',
    'cost',
    '-cost',
    'referenceType',
    '-referenceType',
    'registrationType',
    '-registrationType',
  ]).has(sort);
}

export function crmCustomerLearningRegistrationSelectorPageQuery(
  params: Pick<CrmCustomerLearningQueryParams, 'customerId' | 'q' | 'viewerAccessScope'> & {
    page: number;
  }
) {
  return createQuery<CrmCustomerLearningRegistrationSelectorResponse>({
    path: '/api/v1/registrations',
    queryKey: crmCustomerLearningKeys.registrationSelectorPage(params),
    requestOptions: {
      params: {
        page: params.page,
        pageSize: crmCustomerLearningRegistrationSelectorPageSize,
        q: params.q?.trim() || undefined,
        userProfileId: params.customerId,
      },
    },
  });
}

export function crmCustomerLearningRegistrationPaymentStatusesQuery(
  params: CrmCustomerLearningQueryParams
) {
  return createQuery<RegistrationPaymentStatusCatalogResponse>({
    path: '/api/v1/registrations/payment-statuses',
    queryKey: crmCustomerLearningKeys.registrationPaymentStatuses(params),
  });
}

export function crmCustomerLearningRegistrationReferenceTypesQuery(
  params: CrmCustomerLearningQueryParams
) {
  return createQuery<RegistrationReferenceTypeCatalogResponse>({
    path: '/api/v1/registrations/reference-types',
    queryKey: crmCustomerLearningKeys.registrationReferenceTypes(params),
  });
}

export function crmCustomerLearningRegistrationTypesQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<RegistrationTypeCatalogResponse>({
    path: '/api/v1/registrations/types',
    queryKey: crmCustomerLearningKeys.registrationTypes(params),
  });
}

export function crmCustomerLearningOrganizationsQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<OrganizationSelectorResponse>({
    path: '/api/v1/organizations',
    queryKey: crmCustomerLearningKeys.organizations(params),
  });
}

export function crmCustomerLearningCertificationsQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<CertificationCollectionResponse>({
    path: '/api/v1/certifications',
    queryKey: crmCustomerLearningKeys.certifications(params),
    requestOptions: {
      params: {
        certificationTypeId: params.certificationTypeId,
        certifiedFrom: params.certifiedFrom,
        certifiedTo: params.certifiedTo,
        instructorId: params.instructorId,
        locale: params.locale,
        page: params.page,
        q: params.q,
        sort:
          params.sort.startsWith('certifiedAt') ||
          params.sort.startsWith('expiresAt') ||
          params.sort.startsWith('certificationCode')
            ? params.sort
            : undefined,
        userProfileId: params.customerId,
      },
    },
  });
}

export function crmCustomerLearningCertificationTypesQuery(params: CrmCustomerLearningQueryParams) {
  return createQuery<CertificationTypesResponse>({
    path: '/api/v1/certification-types',
    queryKey: crmCustomerLearningKeys.certificationTypes(params),
    requestOptions: { params: { locale: params.locale } },
  });
}
